import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

// ---------------------------------------------------------------------------
// Page-editor LIBRARY PANEL localization — the left rail of the page editor.
//
// WHY THIS EXISTS: a 2026-08-18 browser reading of the authenticated Studio
// page editor (`?studioDomain=primary&studioView=structure`) under
// `cogo-locale=ko` found the editor chrome rendering English while the
// surrounding IDE shell was Korean: the tab bar read `LAYERS` / `INSERT` and
// the summary line read `1 pages · 2 blocks`.
//
// NOTE ON WHERE THOSE STRINGS LIVE. The reading attributed them to the
// nearest `data-testid` ancestors (`outline-panel` in
// `components/shell/editor-shell.tsx`, `layer-tree` in `layer-tree.tsx`).
// `editor-shell.tsx` has no English literals and is already locale-wired; it
// is only the ancestor. The tab labels are literals in its CHILD
// `library/left-panel.tsx` (which had no locale hook at all), so both
// consumers of this module were greenfield, not "wired by another path".
//
// `LAYERS` / `INSERT` are also a CSS artifact: the source says `Layers` /
// `Insert` and `text-transform: uppercase` (the `uppercase` class on the tab
// buttons) produces the on-screen casing. Searching the repo for the
// on-screen text finds nothing. The uppercase styling is left alone — it is
// presentation, and it applies to the Korean equally.
//
// This module is the single locale-keyed home for the two files in this
// directory that render prose — `left-panel.tsx` (the tab bar) and
// `layer-tree.tsx` (the summary line, the toolbar tooltips, the page context
// menu, and the two empty states). It follows the co-located
// `Record<LocaleCode, …>` + `get…Copy(locale)` shape used by
// `../../page-work-panel-copy.ts` and `shell/ide-v2/surfaces/*-copy.ts`.
//
// TERMINOLOGY — reused from the Korean already shipped in this feature
// (`page-work-panel-copy.ts`) rather than invented, so the two page-editor
// panels do not disagree on the same nouns: 레이어 (layers), 페이지 (page),
// 블록 (block), 이름 변경 (rename), 삭제 (delete), and the ja/zh/zh-TW
// equivalents (レイヤー/图层/圖層, ページ/页面/頁面, ブロック/区块/區塊).
//
// KEPT VERBATIM — deliberately NOT localized:
//  - `page.id` and block `label` (`layer-tree.tsx`). These are project
//    payload — the operator's own identifiers, e.g.
//    `document-1-overview-eyebrow`. They must stay Latin in every locale.
//  - The `Ctrl+F` accelerator inside `search`. A keyboard key, not copy; the
//    physical key does not change with the reader's language.
// ---------------------------------------------------------------------------

export interface LibraryPanelCopy {
  /** `left-panel.tsx` tab bar. Rendered through `text-transform: uppercase`. */
  layersTab: string;
  insertTab: string;

  /**
   * `layer-tree.tsx` summary line — "1 page · 2 blocks".
   *
   * A FUNCTION, not two nouns the caller concatenates, because the counts sit
   * INSIDE the phrase and English inflects on them. The previous JSX
   * (`{app.pages.length} pages · {totalBlocks} blocks`) hardcoded the plural
   * and rendered "1 pages" for a single-page app. Korean/Japanese/Chinese do
   * not inflect, so translating alone would have hidden that defect on the
   * `en` surface instead of fixing it.
   */
  summary: (pages: number, blocks: number) => string;

  /** `layer-tree.tsx` toolbar tooltips. */
  expandAll: string;
  collapseAll: string;
  search: string;
  addPage: string;

  /** `layer-tree.tsx` page context menu. */
  pageOptions: string;
  rename: string;
  duplicate: string;
  delete: string;

  /** `layer-tree.tsx` block row affordance. */
  dragToReorder: string;

  /** `layer-tree.tsx` empty states. */
  noTemplateLoaded: string;
  noBlocks: string;
}

// `en` is byte-identical to what these files rendered before this change,
// with ONE deliberate exception: `summary` now inflects, so a single-page app
// reads "1 page · 1 block" instead of the previous "1 pages · 1 blocks".
const en: LibraryPanelCopy = {
  layersTab: "Layers",
  insertTab: "Insert",

  summary: (pages, blocks) =>
    `${pages} ${pages === 1 ? "page" : "pages"} · ${blocks} ${
      blocks === 1 ? "block" : "blocks"
    }`,

  expandAll: "Expand all",
  collapseAll: "Collapse all",
  search: "Search (Ctrl+F)",
  addPage: "Add page",

  pageOptions: "Page options",
  rename: "Rename",
  duplicate: "Duplicate",
  delete: "Delete",

  dragToReorder: "Drag to reorder",

  noTemplateLoaded: "No template loaded",
  noBlocks: "No blocks",
};

const ko: LibraryPanelCopy = {
  layersTab: "레이어",
  insertTab: "삽입",

  summary: (pages, blocks) => `페이지 ${pages}개 · 블록 ${blocks}개`,

  expandAll: "모두 펼치기",
  collapseAll: "모두 접기",
  search: "검색 (Ctrl+F)",
  addPage: "페이지 추가",

  pageOptions: "페이지 옵션",
  rename: "이름 변경",
  duplicate: "복제",
  delete: "삭제",

  dragToReorder: "드래그하여 순서 변경",

  noTemplateLoaded: "불러온 템플릿이 없습니다",
  noBlocks: "블록 없음",
};

const ja: LibraryPanelCopy = {
  layersTab: "レイヤー",
  insertTab: "挿入",

  summary: (pages, blocks) => `${pages} ページ · ${blocks} ブロック`,

  expandAll: "すべて展開",
  collapseAll: "すべて折りたたむ",
  search: "検索 (Ctrl+F)",
  addPage: "ページを追加",

  pageOptions: "ページオプション",
  rename: "名前を変更",
  duplicate: "複製",
  delete: "削除",

  dragToReorder: "ドラッグして並べ替え",

  noTemplateLoaded: "テンプレートが読み込まれていません",
  noBlocks: "ブロックがありません",
};

const zh: LibraryPanelCopy = {
  layersTab: "图层",
  insertTab: "插入",

  summary: (pages, blocks) => `${pages} 个页面 · ${blocks} 个区块`,

  expandAll: "全部展开",
  collapseAll: "全部折叠",
  search: "搜索 (Ctrl+F)",
  addPage: "添加页面",

  pageOptions: "页面选项",
  rename: "重命名",
  duplicate: "复制",
  delete: "删除",

  dragToReorder: "拖动以重新排序",

  noTemplateLoaded: "未加载模板",
  noBlocks: "暂无区块",
};

const zhTW: LibraryPanelCopy = {
  layersTab: "圖層",
  insertTab: "插入",

  summary: (pages, blocks) => `${pages} 個頁面 · ${blocks} 個區塊`,

  expandAll: "全部展開",
  collapseAll: "全部收合",
  search: "搜尋 (Ctrl+F)",
  addPage: "新增頁面",

  pageOptions: "頁面選項",
  rename: "重新命名",
  duplicate: "複製",
  delete: "刪除",

  dragToReorder: "拖曳以重新排序",

  noTemplateLoaded: "未載入範本",
  noBlocks: "沒有區塊",
};

const LIBRARY_PANEL_COPY: Record<LocaleCode, LibraryPanelCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getLibraryPanelCopy(locale: LocaleCode): LibraryPanelCopy {
  return LIBRARY_PANEL_COPY[locale] ?? en;
}
